import React from 'react';
import { Box, Paper, Typography, IconButton, Menu, MenuItem } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import { ScheduleItemDisplay } from '../../services/projectService';

type ZoomLevel = 'year' | 'month' | 'day';

interface SimpleGanttProps {
  items: ScheduleItemDisplay[];
  zoom?: ZoomLevel;
  onEdit?: (item: ScheduleItemDisplay) => void;
  onDelete?: (id: number) => void;
  onControl?: (item: ScheduleItemDisplay) => void;
  showControl?: boolean;
  visibleColumns?: {
    id?: boolean;
    ssp?: boolean;
    name?: boolean;
    days?: boolean;
    start?: boolean;
    end?: boolean;
    predecessor?: boolean;
    value?: boolean;
    notes?: boolean;
    progress?: boolean;
    progressDate?: boolean;
  };
  showActions?: boolean;
  innerRef?: React.Ref<HTMLDivElement>;
}

// Prosty, lekki wykres Gantta w czystym CSS dopasowany do stylu aplikacji
const SimpleGantt: React.FC<SimpleGanttProps> = ({ items, zoom = 'month', onEdit, onDelete, onControl, showControl, visibleColumns, showActions = true, innerRef }) => {
  // Lokalny stan menu akcji – musi być na górze (przed jakimkolwiek return), aby nie łamać rules-of-hooks
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [menuItemId, setMenuItemId] = React.useState<number | null>(null);

  if (!items || items.length === 0) {
    return (
      <Paper sx={{ mt: 2, p: 2, border: '1px solid #eee' }}>
        <Typography variant="body2" color="text.secondary">Brak zadań do wyświetlenia.</Typography>
      </Paper>
    );
  }

  // Używamy godziny południowej i operacji kalendarzowych, aby uniknąć duplikatów dni przy zmianie czasu (DST)
  const toDate = (v: string) => new Date(v + 'T12:00:00');
  const addDays = (d: Date, n: number) => {
    const copy = new Date(d.getTime());
    copy.setDate(copy.getDate() + n);
    return copy;
  };
  const diffDays = (a: Date, b: Date) => Math.round((a.getTime() - b.getTime()) / (24 * 60 * 60 * 1000));
  const formatDD = (d: Date) => d.toLocaleDateString('pl-PL', { day: '2-digit' });
  const formatMMM = (d: Date) => d.toLocaleDateString('pl-PL', { month: 'short' }).replace('.', '');
  const weekdayAbbr = (dayIdx: number) => {
    switch (dayIdx) {
      case 0: return 'Nd';
      case 1: return 'Pn';
      case 2: return 'Wt';
      case 3: return 'Śr';
      case 4: return 'Czw';
      case 5: return 'Pt';
      case 6: return 'So';
      default: return '';
    }
  };
  const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
  const todayIso = new Date().toISOString().slice(0, 10);
  const computeExpected = (startStr: string, endStr: string): number => {
    const start = new Date(startStr + 'T00:00:00');
    const endExclusive = new Date(endStr + 'T00:00:00');
    const today = new Date(todayIso + 'T12:00:00');
    const total = Math.max(1, Math.round((endExclusive.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)));
    if (today <= start) return 0;
    if (today >= endExclusive) return 100;
    const elapsed = Math.round((today.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
    return clamp((elapsed / total) * 100, 0, 100);
  };

  const min = new Date(Math.min(...items.map(i => toDate(i.startDate).getTime())));
  const maxInclusive = new Date(Math.max(...items.map(i => toDate(i.endDate).getTime())));
  // endDate traktujemy jako DZIEŃ OSTATNIEJ PRACY (inkluzywnie)
  const maxExclusive = addDays(maxInclusive, 1);
  const totalDays = Math.max(1, diffDays(maxExclusive, min));

  const dayWidth = zoom === 'year' ? 8 : zoom === 'month' ? 18 : 28;
  // Lewy padding wykresu: większy dla roku; dla miesięcy/dni + stały margines
  const chartPad = zoom === 'year' ? 64 : (2 * dayWidth + 24);
  const colId = 44;
  const colSSP = 70;
  const colName = 220;
  const colDays = 90;
  const colStart = 130;
  const colEnd = 130;
  const colPred = 140;
  const colValue = 140;
  const colNotes = 160;
  const colProgress = 90;
  const colProgressDate = 140;
  const colActions = 28; // maksymalnie wąska kolumna akcji (3 kropki)
  const colSpacer = 36; // dodatkowy odstęp między tabelą a wykresem słupkowym
  const colVis = {
    id: visibleColumns?.id !== false,
    ssp: visibleColumns?.ssp !== false,
    name: visibleColumns?.name !== false,
    days: visibleColumns?.days !== false,
    start: Boolean(visibleColumns?.start),
    end: Boolean(visibleColumns?.end),
    predecessor: Boolean(visibleColumns?.predecessor),
    value: Boolean(visibleColumns?.value),
    notes: Boolean(visibleColumns?.notes),
    progress: Boolean(visibleColumns?.progress),
    progressDate: Boolean(visibleColumns?.progressDate)
  };
  const leftGridTemplate = [
    showActions ? `${colActions}px` : null,
    colVis.id ? `${colId}px` : null,
    colVis.ssp ? `${colSSP}px` : null,
    colVis.name ? `minmax(${colName}px, 1fr)` : null,
    colVis.days ? `${colDays}px` : null,
    colVis.start ? `${colStart}px` : null,
    colVis.end ? `${colEnd}px` : null,
    colVis.predecessor ? `${colPred}px` : null,
    colVis.value ? `${colValue}px` : null,
    colVis.notes ? `${colNotes}px` : null,
    colVis.progress ? `${colProgress}px` : null,
    colVis.progressDate ? `${colProgressDate}px` : null
  ].filter(Boolean).join(' ');
  const leftWidth =
    (showActions ? colActions : 0) +
    (colVis.id ? colId : 0) +
    (colVis.ssp ? colSSP : 0) +
    (colVis.name ? colName : 0) +
    (colVis.days ? colDays : 0) +
    (colVis.start ? colStart : 0) +
    (colVis.end ? colEnd : 0) +
    (colVis.predecessor ? colPred : 0) +
    (colVis.value ? colValue : 0) +
    (colVis.notes ? colNotes : 0) +
    (colVis.progress ? colProgress : 0) +
    (colVis.progressDate ? colProgressDate : 0) +
    colSpacer;
  const rowHeight = 28; // wysokość pojedynczego wiersza
  const barHeight = 14;
  // const verticalTweak = 0; // nieużywane
  const bottomFudge = 0; // nieużywane po przejściu na osiowe wyrównanie

  const monthHeaderHeight = 22;
  const dayHeaderHeight = 28;
  const headerGap = 8;
  const headerHeight = monthHeaderHeight + dayHeaderHeight + headerGap;

  // Metadane pozycji pasków (względem lewej krawędzi WYKRESU)
  const bars = items.map((i, idx) => {
    const startOffset = Math.max(0, diffDays(toDate(i.startDate), min));
    const duration = Math.max(1, diffDays(addDays(toDate(i.endDate), 1), toDate(i.startDate)));
    const startX = startOffset * dayWidth;
    const endX = (startOffset + duration) * dayWidth;
    const centerY = headerHeight + idx * rowHeight + rowHeight / 2; // oś tekstu i osi słupka
    const topY = centerY - barHeight / 2;
    const bottomY = centerY + barHeight / 2;
    return { idx, idNumber: idx + 1, itemId: i.id, startX, endX, centerY, topY, bottomY };
  });

  // Parsowanie poprzednika, np. "2FS+3dn", "3SS-1dn", fallback: sama liczba → FS+0
  type RelType = 'FS' | 'FF' | 'SS' | 'SF';
  const parsePredecessor = (spec?: string): { predIndex1Based: number; type: RelType; lag: number } | null => {
    if (!spec) return null;
    const s = String(spec).trim();
    const m = s.match(/^(\d+)\s*(FS|FF|SS|SF)([+-]\d+)?/i);
    if (m) {
      return {
        predIndex1Based: Number(m[1]),
        type: m[2].toUpperCase() as RelType,
        lag: m[3] ? Number(m[3]) : 0
      };
    }
    // fallback: sama liczba → FS+0
    const mNum = s.match(/^(\d+)/);
    if (mNum) return { predIndex1Based: Number(mNum[1]), type: 'FS', lag: 0 };
    return null;
  };

  // Połączenia strzałek wyliczone na podstawie poprzedników
  const connections = items.map((it, idx) => {
    const parsed = parsePredecessor(it.predecessor);
    if (!parsed) return null;
    const targetBar = bars[idx];
    // Relacje odniesione do stabilnego ID pozycji (id w projekcie), nie do numeru wiersza
    const sourceBar = bars.find(b => b.itemId === parsed.predIndex1Based) || bars.find(b => b.idNumber === parsed.predIndex1Based);
    if (!sourceBar || !targetBar) return null;

    // Wyznaczenie punktów zakotwiczenia zależnie od typu relacji
    const usesStartOnSource = parsed.type === 'SS' || parsed.type === 'SF';
    const usesStartOnTarget = parsed.type === 'FS' || parsed.type === 'SS';
    const sourceX = usesStartOnSource ? sourceBar.startX : sourceBar.endX;
    const targetX = usesStartOnTarget ? targetBar.startX : targetBar.endX;

    // Punkty start/koniec (dolny róg → górny róg)
    const start = { x: sourceX, y: sourceBar.bottomY };
    const endTop = { x: targetX, y: targetBar.topY };

    return { from: start, to: endTop, sourceBar, targetBar };
  }).filter(Boolean) as Array<{ from: { x: number; y: number }, to: { x: number; y: number }, sourceBar: any, targetBar: any }>;

  const openRowMenu = (e: React.MouseEvent<HTMLButtonElement>, id: number) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuItemId(id);
  };
  const closeRowMenu = () => { setMenuAnchor(null); setMenuItemId(null); };

  

  return (
    <Paper ref={innerRef as any} className="gantt-print-area" sx={{ mt: 2, p: 2, border: '1px solid #eee', overflowX: 'hidden', position: 'relative' }}>
      <Box className="print-wrap" sx={{ display: 'flex', position: 'relative' }}>
        {/* Lewy panel (tabela) */}
        <Box className="left" sx={{ width: 'auto', minWidth: `${leftWidth - colSpacer}px`, backgroundColor: 'white', overflowY: 'hidden' }}>
          <Box sx={{ height: `${monthHeaderHeight}px` }} />
          <Box sx={{ height: `${dayHeaderHeight}px`, display: 'grid', gridTemplateColumns: leftGridTemplate, alignItems: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>
            {showActions && <Box sx={{ pl: 1, fontWeight: 600, lineHeight: `${rowHeight}px` }} />}
            {colVis.id && <Box sx={{ pl: 1, fontWeight: 600, lineHeight: `${rowHeight}px` }}>Id</Box>}
            {colVis.ssp && <Box sx={{ pl: 1, fontWeight: 600, lineHeight: `${rowHeight}px` }}>SSP</Box>}
            {colVis.name && <Box sx={{ pl: 1, fontWeight: 600, lineHeight: `${rowHeight}px` }}>Zadanie</Box>}
            {colVis.days && <Box sx={{ pl: 1, pr: 1, textAlign: 'right', fontWeight: 600, lineHeight: `${rowHeight}px` }}>Czas [dni]</Box>}
            {colVis.start && <Box sx={{ pl: 1, fontWeight: 600, lineHeight: `${rowHeight}px` }}>Rozpoczęcie</Box>}
            {colVis.end && <Box sx={{ pl: 1, fontWeight: 600, lineHeight: `${rowHeight}px` }}>Zakończenie</Box>}
            {colVis.predecessor && <Box sx={{ pl: 1, fontWeight: 600, lineHeight: `${rowHeight}px` }}>Poprzednik</Box>}
            {colVis.value && <Box sx={{ pl: 1, fontWeight: 600, lineHeight: `${rowHeight}px` }}>Wartość</Box>}
            {colVis.notes && <Box sx={{ pl: 1, fontWeight: 600, lineHeight: `${rowHeight}px` }}>Uwagi</Box>}
            {colVis.progress && <Box sx={{ pl: 1, fontWeight: 600, lineHeight: `${rowHeight}px` }}>Zaaw. [%]</Box>}
            {colVis.progressDate && <Box sx={{ pl: 1, fontWeight: 600, lineHeight: `${rowHeight}px` }}>Data stanu</Box>}
            {showActions && <Box sx={{ pl: 1, lineHeight: `${rowHeight}px` }} />}
          </Box>
          <Box sx={{ height: `${headerGap}px` }} />
          <Box>
            {items.map((i, rowIdx) => (
              <Box key={i.id} sx={{ display: 'grid', gridTemplateColumns: leftGridTemplate, alignItems: 'center', height: `${rowHeight}px`, backgroundColor: 'white', borderBottom: '1px solid #f1f1f1' }}>
                {showActions && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: `${rowHeight}px` }}>
                    <IconButton size="small" sx={{ p: 0 }} onClick={(e) => openRowMenu(e, i.id)}>
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </Box>
                )}
                {colVis.id && <Box sx={{ pl: 1, lineHeight: `${rowHeight}px` }}>{i.id}</Box>}
                {colVis.ssp && <Box sx={{ pl: 1, lineHeight: `${rowHeight}px` }}>{i.ssp}</Box>}
                {colVis.name && <Box sx={{ pl: 1, whiteSpace: 'nowrap', lineHeight: `${rowHeight}px` }}>{i.task}</Box>}
                {colVis.days && <Box sx={{ pl: 1, pr: 1, textAlign: 'right', lineHeight: `${rowHeight}px` }}>{i.days}</Box>}
                {colVis.start && <Box sx={{ pl: 1, lineHeight: `${rowHeight}px` }}>{i.startDate}</Box>}
                {colVis.end && <Box sx={{ pl: 1, lineHeight: `${rowHeight}px` }}>{i.endDate}</Box>}
                {colVis.predecessor && <Box sx={{ pl: 1, lineHeight: `${rowHeight}px` }}>{i.predecessor || ''}</Box>}
                {colVis.value && <Box sx={{ pl: 1, lineHeight: `${rowHeight}px` }}>{typeof i.value === 'number' ? i.value : (i.value ?? '')}</Box>}
                {colVis.notes && <Box sx={{ pl: 1, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', lineHeight: `${rowHeight}px` }}>{i.notes || ''}</Box>}
                {colVis.progress && <Box sx={{ pl: 1, textAlign: 'right', pr: 1, lineHeight: `${rowHeight}px` }}>{typeof (i as any).actual_progress === 'number' ? `${(i as any).actual_progress.toFixed(0)}%` : '—'}</Box>}
                {colVis.progressDate && <Box sx={{ pl: 1, lineHeight: `${rowHeight}px` }}>{(i as any).actual_progress_date || '—'}</Box>}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Prawy panel (wykres) */}
        <Box className="right" sx={{ position: 'relative', overflowX: 'auto', overflowY: 'hidden', flex: 1, ml: `${colSpacer}px` }}>
          <Box data-gantt-inner="1" sx={{ position: 'relative', width: `${chartPad + totalDays * dayWidth}px` }}>
            {/* Nagłówki miesięcy */}
            <Box sx={{ height: `${monthHeaderHeight}px`, display: 'grid', gridTemplateColumns: `repeat(${totalDays}, ${dayWidth}px)`, alignItems: 'center', fontSize: '0.75rem', color: 'text.secondary', pl: `${chartPad}px` }}>
              {Array.from({ length: totalDays }).map((_, idx) => {
                const d = addDays(min, idx);
                const isFirstOfMonth = d.getDate() === 1 || idx === 0;
                return (
                  <Box key={`m-${idx}`} sx={{ textAlign: 'center', borderLeft: isFirstOfMonth ? '1px solid #bbb' : '1px solid #f1f1f1', whiteSpace: 'nowrap' }}>
                    {zoom === 'year'
                      ? ((d.getMonth() === 0 && isFirstOfMonth) ? d.getFullYear() : (idx === 0 ? d.getFullYear() : ''))
                      : (isFirstOfMonth ? `${d.getFullYear()} ${formatMMM(d)}` : '')}
                  </Box>
                );
              })}
            </Box>

            {/* Nagłówki dni */}
            <Box sx={{ height: `${dayHeaderHeight}px`, display: 'grid', gridTemplateColumns: `repeat(${totalDays}, ${dayWidth}px)`, alignItems: 'center', fontSize: '0.7rem', color: 'text.secondary', pl: `${chartPad}px` }}>
              {Array.from({ length: totalDays }).map((_, idx) => {
                const d = addDays(min, idx);
                if (zoom === 'year') {
                  return (
                    <Box key={`d-${idx}`} sx={{ textAlign: 'center', borderLeft: '1px solid #f1f1f1' }}>
                      {d.getDate() === 1 ? d.toLocaleDateString('pl-PL', { month: 'short' }).replace('.', '') : ''}
                    </Box>
                  );
                }
                const abbr = weekdayAbbr(d.getDay());
                return (
                  <Box key={`d-${idx}`} sx={{ borderLeft: '1px solid #f1f1f1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
                    <Box sx={{ fontSize: '0.55rem', opacity: 0.85, mb: 0.25 }}>{abbr}</Box>
                    <Box sx={{ fontSize: '0.75rem' }}>{formatDD(d)}</Box>
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ height: `${headerGap}px` }} />

            {/* Siatka wierszy */}
            <Box sx={{ pl: `${chartPad}px` }}>
              {items.map((i) => (
                <Box key={`grid-${i.id}`} sx={{ display: 'grid', gridTemplateColumns: `repeat(${totalDays}, ${dayWidth}px)`, alignItems: 'center', height: `${rowHeight}px` }}>
                  {Array.from({ length: totalDays }).map((_, idx) => (
                    <Box key={`c-${i.id}-${idx}`} sx={{ borderLeft: '1px solid transparent', height: '100%' }} />
                  ))}
                </Box>
              ))}
            </Box>

            {/* Pionowa linia bieżącego dnia */}
            {(() => {
              const today = new Date();
              const todayMid = new Date(today.toISOString().slice(0,10) + 'T00:00:00');
              if (todayMid < min || todayMid >= maxExclusive) return null;
              const offset = diffDays(todayMid, min);
              const left = chartPad + offset * dayWidth;
              return <Box sx={{ position: 'absolute', top: 0, left: `${left}px`, width: 0, height: '100%', borderLeft: '2px dashed #e53935', pointerEvents: 'none', zIndex: 5 }} />;
            })()}

            {/* Weekend shading */}
            {(zoom === 'month' || zoom === 'day') && (
              <Box sx={{ position: 'absolute', top: 0, left: `${chartPad}px`, width: `${totalDays * dayWidth}px`, height: `${headerHeight + items.length * rowHeight}px`, pointerEvents: 'none', zIndex: 1 }}>
                {Array.from({ length: totalDays }).map((_, idx) => {
                  const d = addDays(min, idx);
                  const day = d.getDay();
                  if (day !== 0 && day !== 6) return null;
                  const left = idx * dayWidth;
                  const color = day === 0 ? 'rgba(255, 0, 0, 0.12)' : 'rgba(255, 0, 0, 0.06)';
                  return <Box key={`w-${idx}`} sx={{ position: 'absolute', left: `${left}px`, top: 0, width: `${dayWidth}px`, height: '100%', backgroundColor: color }} />;
                })}
              </Box>
            )}

            {/* Poziome linie rozdzielające wiersze */}
            <Box sx={{ position: 'absolute', top: 0, left: 0, width: `${chartPad + totalDays * dayWidth}px`, height: `${headerHeight + items.length * rowHeight}px`, pointerEvents: 'none', zIndex: 1 }}>
              {items.map((_, rowIdx) => {
                const y = headerHeight + (rowIdx + 1) * rowHeight;
                return (
                  <Box key={`hr-${rowIdx}`} className="grid-line" sx={{ position: 'absolute', top: `${y}px`, left: 0, right: 0, borderTop: '1px solid #f1f1f1' }} />
                );
              })}
            </Box>

            {/* Słupki */}
            <Box sx={{ position: 'absolute', top: 0, left: `${chartPad}px`, width: `${totalDays * dayWidth}px`, height: `${headerHeight + items.length * rowHeight}px`, pointerEvents: 'none', zIndex: 2 }}>
              {items.map((i, rowIdx) => {
                const startOffset = Math.max(0, diffDays(toDate(i.startDate), min));
                const duration = Math.max(1, diffDays(addDays(toDate(i.endDate), 1), toDate(i.startDate)));
                const left = startOffset * dayWidth;
                const centerY = headerHeight + rowIdx * rowHeight + rowHeight / 2;
                const top = centerY - barHeight / 2;
                const width = duration * dayWidth;

                const expectedPct = Math.round(computeExpected(i.startDate, i.endDate));
                const actualPct = typeof (i as any).actual_progress === 'number' ? clamp((i as any).actual_progress, 0, 100) : undefined;
                const isStarted = new Date(todayIso) >= toDate(i.startDate);
                const showBadge = Boolean(showControl) && (isStarted || (actualPct ?? 0) > 0 || new Date(todayIso) > toDate(i.endDate));
                const badgeActual = typeof actualPct === 'number' ? Math.round(actualPct) : '-';
                const badgeText = `${badgeActual}/${expectedPct}`;
                const isGood = typeof badgeActual === 'number' ? (badgeActual >= expectedPct) : false;
                const coreColor = isGood ? '#2e7d32' : '#d32f2f';
                const labelColor = coreColor;
                const coreWidth = width * ((actualPct ?? 0) / 100);
                return (
                  <React.Fragment key={`bar-${i.id}`}>
                    <Box sx={{ position: 'absolute', left: `${left}px`, top: `${top}px`, width: `${width}px`, height: `${barHeight}px`, background: '#6fb3ff', borderRadius: 0 }} />
                    {Boolean(showControl) && typeof actualPct === 'number' && actualPct > 0 && (
                      <Box sx={{ position: 'absolute', left: `${left}px`, top: `${centerY - 3}px`, width: `${coreWidth}px`, height: '6px', background: coreColor, borderRadius: '3px' }} />
                    )}
                    {showBadge && (
                      <Box sx={{ position: 'absolute', left: `${left}px`, top: `${centerY}px`, transform: 'translate(-110%, -50%)', color: labelColor, fontSize: '0.6rem', fontWeight: 600 }}>
                        {badgeText}
                      </Box>
                    )}
                  </React.Fragment>
                );
              })}
            </Box>

            {/* Strzałki zależności */}
            {connections.length > 0 && (
              <Box sx={{ position: 'absolute', top: 0, left: `${chartPad}px`, width: `${totalDays * dayWidth}px`, height: `${headerHeight + items.length * rowHeight}px`, pointerEvents: 'none', zIndex: 3 }}>
                <svg width={totalDays * dayWidth} height={headerHeight + items.length * rowHeight} style={{ overflow: 'visible' }}>
                  <defs>
                    <marker id="arrowheadDown" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="5.625" markerHeight="5.625" orient="auto">
                      <path d="M 0 0 L 10 5 L 0 10 z" fill="#424242" />
                    </marker>
                  </defs>
                  {connections.map((c, i) => {
                    const gapMargin = 4;
                    const laneBelowSourceY = Math.min(c.sourceBar.bottomY + gapMargin, headerHeight + items.length * rowHeight - 2);
                    const laneAboveTargetY = Math.max(c.targetBar.topY - gapMargin, headerHeight + 2);

                    const p0x = c.from.x;
                    const p0y = c.from.y;
                    const p1x = p0x;
                    const p1y = laneBelowSourceY;
                    const p2x = c.to.x; // prowadź poziomo do osi celu
                    const p2y = laneBelowSourceY;
                    const p3x = c.to.x; // pionowo nad celem – brak schodka
                    const p3y = laneAboveTargetY;
                    const p4x = c.to.x;
                    const p4y = laneAboveTargetY;
                    const p5x = c.to.x;
                    const p5y = c.to.y;

                    const d = `M ${p0x} ${p0y} L ${p1x} ${p1y} L ${p2x} ${p2y} L ${p3x} ${p3y} L ${p4x} ${p4y} L ${p5x} ${p5y}`;

                    return (
                      <path key={`conn-${i}`} d={d} fill="none" stroke="#424242" strokeWidth={1.40625} markerEnd="url(#arrowheadDown)" opacity={0.9} />
                    );
                  })}
                </svg>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Menu akcji wiersza */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeRowMenu}>
        <MenuItem onClick={() => {
          const row = items.find(x => x.id === menuItemId);
          if (row && onEdit) onEdit(row);
          closeRowMenu();
        }}>Edytuj</MenuItem>
        <MenuItem onClick={() => {
          const row = items.find(x => x.id === menuItemId);
          if (row && onControl) onControl(row);
          closeRowMenu();
        }}>Kontrola zaawansowania</MenuItem>
        <MenuItem onClick={() => {
          if (menuItemId != null && onDelete) onDelete(menuItemId);
          closeRowMenu();
        }} sx={{ color: 'error.main' }}>Usuń</MenuItem>
      </Menu>
    </Paper>
  );
};

export default SimpleGantt;


